import { useNavigate, useSearchParams } from 'react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { addToast, Button, Form, Input, Spinner } from '@heroui/react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { useTheme } from '@/ui/theme'
import { useCancelCover } from '@/hooks/use-cancel-cover'
import { useAppSelector } from '@/redux/hooks'

const schema = z.object({
  orderNumber: z
    .string()
    .min(1, { message: 'cover_cancellation.order_number_required' })
    .trim(),
  email: z.email({ message: 'cover_cancellation.email_invalid' })
})

type CoverCancellationFormData = z.infer<typeof schema>

export const CoverCancellationForm = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const posthog = usePostHog()

  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const orderNumberParam = searchParams.get('orderNumber')
  const emailParam = searchParams.get('email')

  const {
    register,
    formState: { errors },
    handleSubmit
  } = useForm<CoverCancellationFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      orderNumber: orderNumberParam ?? '',
      email: emailParam ?? ''
    },
    mode: 'onChange'
  })

  const { mutate, isPending } = useCancelCover()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const handleCancel = (values: CoverCancellationFormData) => {
    if (!ecommerceId) return

    posthog.capture('Cover Cancellation Started', {
      ecommerceId,
      orderNumber: values.orderNumber,
      email: values.email
    })

    mutate(
      {
        ecommerceId,
        returnsCancelWfpContractRequest: {
          customer_printed_order_id: values.orderNumber,
          email: values.email
        }
      },
      {
        onError: () => {
          addToast({
            title: t('cover_cancellation.error_toast_title'),
            description: t('cover_cancellation.error_toast_description'),
            color: 'danger'
          })
        },
        onSuccess: () => {
          posthog.capture('Cover Cancellation Submitted', {
            ecommerceId,
            orderNumber: values.orderNumber,
            email: values.email
          })

          navigate('success', {
            relative: 'path'
          })
        }
      }
    )
  }

  const rounded = theme.borderRadius

  return (
    <Form className="w-full space-y-4" onSubmit={handleSubmit(handleCancel)}>
      <div className="w-full space-y-3">
        <Input
          {...register('orderNumber')}
          type="text"
          label={t('cover_cancellation.order_number_label')}
          placeholder={t('cover_cancellation.order_number_placeholder')}
          radius="sm"
          variant="bordered"
          autoFocus
          isInvalid={!!errors.orderNumber}
          errorMessage={
            errors.orderNumber?.message
              ? t(errors.orderNumber.message)
              : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
        <Input
          {...register('email')}
          type="email"
          autoComplete="email"
          label={t('cover_cancellation.email_label')}
          placeholder={t('cover_cancellation.email_placeholder')}
          radius="sm"
          variant="bordered"
          isInvalid={!!errors.email}
          errorMessage={
            errors.email?.message ? t(errors.email.message) : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
      </div>
      <Button
        type="submit"
        variant="bordered"
        color="danger"
        size="lg"
        fullWidth
        isLoading={isPending}
        spinner={<Spinner size="sm" />}
        style={{ borderRadius: rounded }}
      >
        {t('cover_cancellation.submit')}
      </Button>
    </Form>
  )
}
